<template>
  <article>
    <activity-stage :customRootContainerClass="'line-by-line'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <figure id="lbyl-figure">
          <img
            :alt="currentRound.contentImage?.image.description"
            :src="currentRound.contentImage?.image.src"
          />
        </figure>
        <text-display-list :text-size="isMobile ? 'mobileFontSize' : currentRound.textSize">
          <h2 class="title" v-if="currentRound.optionalTitle">
            {{ currentRound.optionalTitle }}
          </h2>
          <li v-for="(step, stepIdx) of currentRound.steps" :key="step.answer">
            <blinking-cursor-text :text="step.answer" :text-step="stepIdx"></blinking-cursor-text>
          </li>
        </text-display-list>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },
  mixins: [TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'LeftImageSentences'
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },
  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();

      this.createRounds(roundSetup);
      await this.loadImages();
    },

    getKeyboardConfig() {
      return {
        hideNumberRow: !this.activityData.keyboard.showNumbers
      };
    },

    async loadImages() {
      this.rounds.forEach(({ contentImage }) => {
        this.queueImage(contentImage.image.src);
      });

      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          voice: `${this.voiceInstructionsPrefix}/linebyline-${this.getRandomVoice()}-1.mp3`,
          written: 'Type each sentence. Use the Enter key to go to the next line.'
        }
      };
    },

    startActivity() {
      this.$nextTick(this.setActivityContainerHeight);

      const timeline = new TimelineLite();

      timeline
        .call(this.setupNewRound)
        .call(this.enableKeyboard)
        .call(this.errorLoggerStart)
        .to('.activity-stage', 0.5, { opacity: 1 });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.handleStepEnd();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.sndBell();
      this.finishStep();
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .delay(1)
          .to('.activity-stage', 0.5, { opacity: 0 })
          .call(this.setupNewRound)
          .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.line-by-line {
  opacity: 0;

  article {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    figure {
      height: 100%;
      max-width: 300px;
      position: relative;
      margin: 0 20px;
      text-align: center;

      img {
        height: 100%;
        width: 100%;
      }
    }

    ul,
    .title {
      padding-left: 10px;
      flex: 1;
      list-style: none;
      font-size: 25px;
      align-self: flex-start;

      &.medium {
        font-size: 24px;
      }

      &.small {
        font-size: 20px;
      }

      &.mobileFontSize {
        font-size: 19px;
      }
    }

    .title {
      font-weight: bold;
      margin-bottom: 10px;
      padding-left: 0px;
    }
  }
}

.is-chrome-ipad {
  figure {
    align-self: flex-start;
  }
}
</style>
